import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import axiosInstance from "../utils/axiosInstance";
import { AVAILABLE_UI_LANGUAGES } from "../constants/languages";
import { ORGANISATION_ROLES } from "../utils/roles";
import FormModal from "./admin/FormModal";
import Button from "./ui/Button";
import StatusBadge from "./ui/StatusBadge";

const EditUserModal = ({
  isOpen,
  onClose,
  user,
  organisationId,
  onUserUpdated,
  showArchiveActions = false,
}) => {
  const [submissionError, setSubmissionError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [archiveError, setArchiveError] = useState("");
  const [archiving, setArchiving] = useState(false);

  const requestConfig = organisationId ? { params: { organisationId } } : {};

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstname: user?.firstname || "",
      lastname: user?.lastname || "",
      email: user?.email || "",
      companyRole: user?.companyRole || user?.role || ORGANISATION_ROLES.USER,
      uiLanguage: user?.languages?.ui || "English",
      translateFrom: (user?.languages?.translateFrom || []).join(", "),
      translateTo: (user?.languages?.translateTo || []).join(", "),
      hourlyCost:
        user?.hourlyCost !== null && user?.hourlyCost !== undefined ? user.hourlyCost : "",
      hourlyRate:
        user?.hourlyRate !== null && user?.hourlyRate !== undefined ? user.hourlyRate : "",
    },
    validationSchema: Yup.object({
      firstname: Yup.string().required("First name is required"),
      lastname: Yup.string().required("Last name is required"),
      email: Yup.string()
        .email("Invalid email format")
        .required("Email is required"),
      uiLanguage: Yup.string().required("UI Language is required"),
      hourlyCost: Yup.number().min(0, "Must be 0 or greater").nullable(),
      hourlyRate: Yup.number().min(0, "Must be 0 or greater").nullable(),
    }),
    onSubmit: async (values) => {
      if (!user?._id) return;
      try {
        setSubmissionError("");

        const translateFromArray = values.translateFrom
          ? values.translateFrom.split(",").map((l) => l.trim())
          : [];
        const translateToArray = values.translateTo
          ? values.translateTo.split(",").map((l) => l.trim())
          : [];

        const payload = {
          firstname: values.firstname,
          lastname: values.lastname,
          email: values.email,
          companyRole: values.companyRole,
          uiLanguage: values.uiLanguage,
          translateFrom: translateFromArray,
          translateTo: translateToArray,
        };

        if (values.hourlyCost !== "" && values.hourlyCost !== null) {
          payload.hourlyCost = parseFloat(values.hourlyCost) || null;
        }
        if (values.hourlyRate !== "" && values.hourlyRate !== null) {
          payload.hourlyRate = parseFloat(values.hourlyRate) || null;
        }

        const response = await axiosInstance.put(`/users/${user._id}`, payload, requestConfig);

        if (onUserUpdated) onUserUpdated(response.data);
        onClose();
      } catch (error) {
        console.error("Error updating user:", error);
        setSubmissionError(
          error.response?.data?.message ||
            "An error occurred while updating the user."
        );
      }
    },
  });

  const handleSetPassword = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");
    const pwd = e.target.elements?.adminPassword?.value;

    if (!pwd || pwd.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return;
    }
    if (!user?._id) {
      setPasswordError("No user selected");
      return;
    }

    try {
      await axiosInstance.post(`/users/${user._id}/set-password`, { password: pwd });
      setPasswordSuccess(
        "Password updated successfully. User will be required to change it on next login."
      );
      e.target.elements.adminPassword.value = "";
    } catch (error) {
      console.error("Error setting password:", error);
      setPasswordError(
        error.response?.data?.message ||
          "An error occurred while setting the password."
      );
    }
  };

  const handleToggleArchive = async () => {
    if (!user?._id) return;
    const action = user.isArchived ? "unarchive" : "archive";
    if (!confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} this user?`)) return;

    setArchiving(true);
    setArchiveError("");
    try {
      if (user.isArchived) {
        const response = await axiosInstance.put(
          `/users/${user._id}`,
          { isArchived: false },
          requestConfig
        );
        if (onUserUpdated) onUserUpdated(response.data);
      } else {
        await axiosInstance.delete(`/users/${user._id}`, requestConfig);
        if (onUserUpdated) onUserUpdated({ ...user, isArchived: true });
        onClose();
      }
    } catch (error) {
      setArchiveError(
        error.response?.data?.message || "Failed to update account status"
      );
    } finally {
      setArchiving(false);
    }
  };

  const handleClose = () => {
    setSubmissionError("");
    setPasswordError("");
    setPasswordSuccess("");
    setArchiveError("");
    onClose();
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit User"
      size="xl"
    >
      <form onSubmit={formik.handleSubmit} className="space-y-4">
        <div className="flex gap-4">
          <div className="flex-1">
            <label htmlFor="firstname" className="block text-sm font-medium text-gray-700">
              First Name
            </label>
            <input
              id="firstname"
              name="firstname"
              type="text"
              value={formik.values.firstname}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full border rounded px-3 py-2"
            />
            {formik.touched.firstname && formik.errors.firstname && (
              <div className="text-red-500 text-sm">{formik.errors.firstname}</div>
            )}
          </div>
          <div className="flex-1">
            <label htmlFor="lastname" className="block text-sm font-medium text-gray-700">
              Last Name
            </label>
            <input
              id="lastname"
              name="lastname"
              type="text"
              value={formik.values.lastname}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full border rounded px-3 py-2"
            />
            {formik.touched.lastname && formik.errors.lastname && (
              <div className="text-red-500 text-sm">{formik.errors.lastname}</div>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            Email
          </label>
          <input
            id="email"
            name="email"
            type="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="w-full border rounded px-3 py-2"
          />
          {formik.touched.email && formik.errors.email && (
            <div className="text-red-500 text-sm">{formik.errors.email}</div>
          )}
        </div>

        <div>
          <label htmlFor="companyRole" className="block text-sm font-medium text-gray-700">
            Department Role
          </label>
          <select
            id="companyRole"
            name="companyRole"
            value={formik.values.companyRole}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="w-full border rounded px-3 py-2"
          >
            <option value={ORGANISATION_ROLES.USER}>User</option>
            <option value={ORGANISATION_ROLES.ORGANISATION_ADMIN}>Department Admin</option>
          </select>
        </div>

        <div>
          <label htmlFor="uiLanguage" className="block text-sm font-medium text-gray-700">
            UI Language
          </label>
          <select
            id="uiLanguage"
            name="uiLanguage"
            value={formik.values.uiLanguage}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="w-full border rounded px-3 py-2"
          >
            {AVAILABLE_UI_LANGUAGES.map((lang) => (
              <option key={lang} value={lang}>
                {lang}
              </option>
            ))}
          </select>
          {formik.touched.uiLanguage && formik.errors.uiLanguage && (
            <div className="text-red-500 text-sm">{formik.errors.uiLanguage}</div>
          )}
        </div>

        <div>
          <label htmlFor="translateFrom" className="block text-sm font-medium text-gray-700">
            Translate From Languages
          </label>
          <input
            id="translateFrom"
            name="translateFrom"
            type="text"
            value={formik.values.translateFrom}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="w-full border rounded px-3 py-2"
            placeholder="E.g., English, French"
          />
        </div>

        <div>
          <label htmlFor="translateTo" className="block text-sm font-medium text-gray-700">
            Translate To Languages
          </label>
          <input
            id="translateTo"
            name="translateTo"
            type="text"
            value={formik.values.translateTo}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="w-full border rounded px-3 py-2"
            placeholder="E.g., Spanish, German"
          />
        </div>

        <div className="flex gap-4">
          <div className="flex-1">
            <label htmlFor="hourlyCost" className="block text-sm font-medium text-gray-700">
              Hourly Cost (SEK)
            </label>
            <input
              id="hourlyCost"
              name="hourlyCost"
              type="number"
              step="0.01"
              min="0"
              value={formik.values.hourlyCost}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full border rounded px-3 py-2"
              placeholder="Company cost per hour"
            />
            {formik.touched.hourlyCost && formik.errors.hourlyCost && (
              <div className="text-red-500 text-sm">{formik.errors.hourlyCost}</div>
            )}
          </div>
          <div className="flex-1">
            <label htmlFor="hourlyRate" className="block text-sm font-medium text-gray-700">
              Hourly Rate (SEK)
            </label>
            <input
              id="hourlyRate"
              name="hourlyRate"
              type="number"
              step="0.01"
              min="0"
              value={formik.values.hourlyRate}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full border rounded px-3 py-2"
              placeholder="Customer debit rate"
            />
            {formik.touched.hourlyRate && formik.errors.hourlyRate && (
              <div className="text-red-500 text-sm">{formik.errors.hourlyRate}</div>
            )}
          </div>
        </div>

        {submissionError && <div className="text-red-500 text-sm">{submissionError}</div>}

        <div className="flex justify-end space-x-2">
          <button type="button" onClick={handleClose} className="px-4 py-2 bg-gray-300 rounded">
            Cancel
          </button>
          <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">
            Save Changes
          </button>
        </div>
      </form>

      <hr className="my-6" />

      <div>
        <h3 className="text-lg font-semibold mb-2">Set / Reset Password</h3>
        <form onSubmit={handleSetPassword} className="space-y-2">
          <input
            id="adminPassword"
            name="adminPassword"
            type="password"
            placeholder="New temporary password"
            className="w-full border rounded px-3 py-2"
          />
          {passwordError && <div className="text-red-500 text-sm">{passwordError}</div>}
          {passwordSuccess && <div className="text-green-600 text-sm">{passwordSuccess}</div>}
          <div className="flex justify-end space-x-2 mt-2">
            <button
              type="button"
              onClick={() => {
                setPasswordError("");
                setPasswordSuccess("");
              }}
              className="px-4 py-2 bg-gray-300 rounded"
            >
              Clear
            </button>
            <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded">
              Set Password
            </button>
          </div>
        </form>
      </div>

      {showArchiveActions && (
        <>
          <hr className="my-6" />
          <div>
            <h3 className="text-lg font-semibold mb-2">Account Status</h3>
            <div className="flex items-center gap-3 mb-3">
              <StatusBadge status={user?.isArchived ? "archived" : "active"} />
              <span className="text-sm text-gray-600">
                {user?.isArchived
                  ? "This user is archived and hidden from the team list."
                  : "This user is active in the department."}
              </span>
            </div>
            {archiveError && <div className="text-red-500 text-sm mb-3">{archiveError}</div>}
            <Button
              onClick={handleToggleArchive}
              variant={user?.isArchived ? "solid" : "danger"}
              size="md"
              disabled={archiving}
            >
              {archiving
                ? "Updating…"
                : user?.isArchived
                  ? "Unarchive"
                  : "Archive"}
            </Button>
          </div>
        </>
      )}
    </FormModal>
  );
};

export default EditUserModal;
